import { Component, computed, Input, OnInit } from '@angular/core'
import { Params, Router } from '@angular/router'
import { DEFAULT_LOGIN_ROUTE, RETURN_TO_DESKTOP, RETURN_URL_KEY, RootParams } from '@ftr/api-shared'
import { DJ_COURT_SYSTEM_ID, DJ_KEY } from '@ftr/contracts/digital-justice'
import { IdentityProviderName } from '@ftr/contracts/type/account'
import { BadgeColors, Icon, LocalStorageService, NotificationType, PageStyle } from '@ftr/foundation'
import { AppPaths } from '@ftr/routing-paths'
import { featureFlag } from '@ftr/ui-feature-flags'
import { BehaviorSubject } from 'rxjs'
import { AuthenticationService } from '../../services'
import { isPublicPortal } from '../../util'

interface LoginConfig {
  title: string
  subTitle: string
  icon?: Icon
  badgeColor?: BadgeColors
}

@Component({
  selector: 'ftr-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  host: {
    class: 'login page',
  },
  standalone: false,
})
export class LoginComponent implements OnInit {
  @Input() returnUrl: string | undefined
  @Input() identityProvider: IdentityProviderName | undefined
  @Input() codeChallenge: string | undefined
  @Input() state: string | undefined
  @Input() authorizationCode: string | undefined
  @Input() showMockLogin: boolean

  isHandlingNativeAuth = false
  isAccessingSharedAudio = false

  readonly pageStyle = PageStyle.Narrow
  readonly notificationType = NotificationType

  isPublicNavigationEnabled = featureFlag('public-navigation')

  loginConfig = computed<LoginConfig>(() => this.getLoginConfig())

  constructor(
    private localStorageService: LocalStorageService,
    private readonly router: Router,
    private authenticationService: AuthenticationService,
  ) {}

  ngOnInit(): void {
    this.setReturnUrl()
    this.setAccessingSharedAudio()
    this.setDJLocalStorageItems()
    this.handleNativeAuth()
  }

  private setReturnUrl(): void {
    if (!this.returnUrl) {
      const storedReturnUrl = this.localStorageService.getGlobal<string>(RETURN_URL_KEY)

      if (storedReturnUrl && storedReturnUrl !== DEFAULT_LOGIN_ROUTE) {
        this.returnUrl = storedReturnUrl

        // make it obvious when a returnUrl will be used
        this.router.navigate([AppPaths.Login], {
          queryParams: { [RootParams.ReturnUrl]: storedReturnUrl },
          replaceUrl: true,
        })
      }
    }
  }

  private setAccessingSharedAudio(): void {
    if (this.returnUrl) {
      this.isAccessingSharedAudio = this.returnUrl.startsWith(`/${AppPaths.SharedWithMe}`)
    }
  }

  private setDJLocalStorageItems(): void {
    // The library typings are incorrect, this is in fact a BehaviorSubject
    const djKey = (this.router.routerState.root.queryParams as BehaviorSubject<Params>)?.value?.[DJ_KEY]
    djKey && this.localStorageService.setGlobal(DJ_KEY, djKey)
    const djCsId = (this.router.routerState.root.queryParams as BehaviorSubject<Params>)?.value?.[DJ_COURT_SYSTEM_ID]
    djCsId && this.localStorageService.setGlobal(DJ_COURT_SYSTEM_ID, djCsId)
  }

  private handleNativeAuth(): void {
    if (this.identityProvider && this.codeChallenge && this.state) {
      this.isHandlingNativeAuth = true

      this.localStorageService.setGlobal(RETURN_TO_DESKTOP, true)

      this.authenticationService.loginWithRedirect(
        this.identityProvider,
        this.returnUrl,
        this.codeChallenge,
        this.state,
        true,
      )
    }
  }

  private getLoginConfig(): LoginConfig {
    if (!this.isPublicNavigationEnabled()) {
      return {
        title: 'Log In',
        subTitle: 'For The Record delivers fast, secure access to the court record.',
      }
    }
    if (isPublicPortal()) {
      return {
        icon: 'PeopleLine',
        badgeColor: BadgeColors.Blueberry,
        title: 'Log in to Public Portal',
        subTitle: 'Request court recordings or transcripts.',
      }
    }
    return {
      icon: 'Courthouse',
      badgeColor: BadgeColors.Mint,
      title: 'Log in to Court Portal',
      subTitle: 'Access and manage court content.',
    }
  }
}
